import { useState } from "react";
import { useParams, Link } from "wouter";
import { useGetClause, getGetClauseQueryKey } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Pencil, Clock, User, MapPin, Tag, BookOpen, History } from "lucide-react";
import type { ClauseDetail as ClauseDetailType } from "@workspace/api-client-react";
import { sanitizeHtml } from "@/lib/sanitize";
import { format } from "date-fns";
import { renderMergeFields, MERGE_FIELDS } from "@/lib/mergeFields";
import { useOrg } from "@/lib/orgContext";

const CATEGORY_LABELS: Record<string, string> = {
  compensation: "Compensation",
  termination: "Termination",
  buyout: "Buyout",
  nil: "NIL",
  title_ix: "Title IX",
  indemnification: "Indemnification",
  governing_law: "Governing Law",
  outside_income: "Outside Income",
  general: "General",
};

const CATEGORY_COLORS: Record<string, string> = {
  compensation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  termination: "bg-red-50 text-red-700 border-red-200",
  buyout: "bg-orange-50 text-orange-700 border-orange-200",
  nil: "bg-purple-50 text-purple-700 border-purple-200",
  title_ix: "bg-blue-50 text-blue-700 border-blue-200",
  indemnification: "bg-slate-50 text-slate-700 border-slate-200",
  governing_law: "bg-gray-50 text-gray-700 border-gray-200",
  outside_income: "bg-yellow-50 text-yellow-700 border-yellow-200",
  general: "bg-muted text-muted-foreground border-border",
};

const SAMPLE_CONTEXT = MERGE_FIELDS.reduce<Record<string, string>>((acc, f) => {
  acc[f.key] = f.example;
  return acc;
}, {});

export default function ClauseDetail() {
  const { id } = useParams<{ id: string }>();
  const clauseId = parseInt(id, 10);
  const { org } = useOrg();
  const isAdmin = org?.role === "admin";

  const { data: clause, isLoading } = useGetClause(clauseId, {
    query: { queryKey: getGetClauseQueryKey(clauseId) },
  });

  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[300px]" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  if (!clause) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Clause not found.</p>
        <Button variant="outline" asChild className="mt-4">
          <Link href="/clause-library">Back to Library</Link>
        </Button>
      </div>
    );
  }

  const clauseDetail = clause as ClauseDetailType;
  const versions = clauseDetail.versions ?? [];
  const displayVersionId = selectedVersionId ?? clause.latestVersion?.id ?? null;
  const displayVersion = displayVersionId
    ? versions.find((v) => v.id === displayVersionId) ?? clause.latestVersion
    : clause.latestVersion;

  const renderedBody = displayVersion
    ? showPreview
      ? renderMergeFields(displayVersion.body, SAMPLE_CONTEXT)
      : displayVersion.body
    : "";

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link href="/clause-library">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight">{clause.title}</h1>
              <Badge variant="outline" className={CATEGORY_COLORS[clause.category] ?? ""}>
                {CATEGORY_LABELS[clause.category] ?? clause.category}
              </Badge>
              {clause.isSeed && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Starter</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {versions.length} version{versions.length !== 1 ? "s" : ""} · Updated {format(new Date(clause.updatedAt), "MMM d, yyyy")}
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button variant="outline" asChild>
            <Link href={`/clause-library/${clauseId}/edit`}>
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Link>
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  {displayVersion ? `Version ${displayVersion.versionNumber}` : "Clause Body"}
                  {displayVersionId === clause.latestVersion?.id && (
                    <Badge variant="outline" className="text-xs ml-1">Latest</Badge>
                  )}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview((p) => !p)}
                >
                  {showPreview ? "View raw" : "Preview with examples"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {displayVersion ? (
                <div
                  className="text-sm leading-relaxed whitespace-pre-wrap font-mono bg-muted/20 p-4 rounded-md border prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: showPreview ? sanitizeHtml(renderedBody) : sanitizeHtml(displayVersion.body.replace(/\{\{([^}]+)\}\}/g, '<span class="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-1 rounded font-mono text-xs">{{$1}}</span>')) }}
                />
              ) : (
                <p className="text-muted-foreground text-sm italic">No content yet.</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {clause.jurisdiction && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Jurisdiction</div>
                    <div>{clause.jurisdiction}</div>
                  </div>
                </div>
              )}
              {clause.approvingAttorney && (
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Approving Attorney</div>
                    <div>{clause.approvingAttorney}</div>
                  </div>
                </div>
              )}
              {clause.lastReviewedAt && (
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Last Reviewed</div>
                    <div>{format(new Date(clause.lastReviewedAt), "MMMM d, yyyy")}</div>
                  </div>
                </div>
              )}
              {(clause.tags as string[]).length > 0 && (
                <div className="flex items-start gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="text-xs text-muted-foreground font-medium mb-1">Tags</div>
                    <div className="flex flex-wrap gap-1">
                      {(clause.tags as string[]).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {versions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-4 w-4 text-muted-foreground" /> Version History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {versions.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVersionId(v.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm border transition-colors ${
                      displayVersionId === v.id
                        ? "border-primary bg-primary/5"
                        : "border-transparent hover:bg-muted"
                    }`}
                  >
                    <div className="font-medium">Version {v.versionNumber}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {format(new Date(v.createdAt), "MMM d, yyyy")}
                      {v.id === clause.latestVersion?.id && (
                        <span className="ml-2 text-primary font-medium">Latest</span>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
